import { wrapperTemplate, dotListTemplate, dotItemTemplate } from "./templates";
import { applyStyles, getSlide, getSlidesTrack, animateTrack, getSlideIden } from "./utils";

const trackClassName = "slick-track";
const activeDotClassName = "slick-active";
const trackCssSelector = `.${trackClassName}`;

function wrapSlides(container, config) {
    const wrapper = wrapperTemplate.getElement();
    const innerWrapper = wrapper.querySelector(trackCssSelector);

    if (!config.vertical) {
        wrapper.style.whiteSpace = config.whiteSpace;
    }

    if (config.fullscreen) {
        document.body.style.overflow = "hidden";
    }

    container.classList.add("slick-initialized");
    container.classList.add("slick-slider");

    applyStyles(innerWrapper, {
        ...config.trackStyles,
        transition: `all ${config.speed}ms ease-out 0s`,
    });

    const itemWidth = container.offsetWidth / config.slidesToShow;
    Array.from(container.children).forEach((slide, idx) => {
        if (!config.vertical) {
            applyStyles(slide, {
                display: "inline-block",
                height: "100%",
                width: `${itemWidth}px`,
                height: "auto",
                ...config.slideStyles
            });
        }
        slide.setAttribute("data-slider-item-id", idx);
        innerWrapper.appendChild(slide);
    });

    applyStyles(container, {
        maxHeight: "none",
    });

    container.appendChild(wrapper);
}

function nextSlide(container, config, context) {
    const track = getSlidesTrack(container);
    const lastPageSlideId = track.children.length - config.slidesToShow;
    let currentSlideId = context.getActiveSlide() + config.slidesToScroll;
    
    if(currentSlideId > lastPageSlideId)
        currentSlideId = context.getActiveSlide() + 1;

    const nextElement = getSlide(container, currentSlideId);

    if (currentSlideId <= lastPageSlideId) {
        animateTrack(track, nextElement, config);
        context.setActiveSlide(currentSlideId);
        setActiveDot(container, config, context);
        config.slideCallback(nextElement);
        setupAutoplay(container, config, context);
        return true;
    } else if (config.infinite) {
        const firstElement = track.children[0];
        animateTrack(track, firstElement, config);
        context.resetActiveSlide();
        setActiveDot(container, config, context);
        config.slideCallback(firstElement);
        setupAutoplay(container, config, context);
        return true;
    } else {
        return false;
    }
}

function prevSlide(container, config, context) {
    let currentSlideId = context.getActiveSlide() - config.slidesToScroll;

    if (currentSlideId < 0)
        currentSlideId = context.getActiveSlide() - 1;

    const track = getSlidesTrack(container);
    const prevElement = getSlide(container, currentSlideId);

    if (prevElement) {
        animateTrack(track, prevElement, config);
        context.setActiveSlide(currentSlideId);
        setActiveDot(container, config, context);
        config.slideCallback(prevElement);
        setupAutoplay(container, config, context);
        return true;
    } else if (config.infinite) {
        const slideId = context.setActiveSlide(track.children.length - config.slidesToShow);
        const slide = getSlide(container, slideId);
        animateTrack(track, slide, config);
        setActiveDot(container, config, context);
        config.slideCallback(slide);
        setupAutoplay(container, config, context);
        return true;
    } else {
        return false;
    }
}

function renderNavigationButtons(container, config, context) {
    if (!(config.arrows && config.nextArrow && config.prevArrow)) {
        return;
    }

    let prevButton, nextButton;

    if (config.useExistingArrows) {
        prevButton = config.prevArrow;
        nextButton = config.nextArrow;
    } else {
        container.insertAdjacentHTML("afterbegin", config.prevArrow);
        container.insertAdjacentHTML("beforeend", config.nextArrow);
    
        prevButton = container.firstElementChild;
        nextButton = container.lastElementChild;
    
        [prevButton, nextButton].forEach((button) => {
            applyStyles(button, {
                cursor: "pointer",
            });
            button.classList.add("slick-arrow");
        });
    }

    nextButton.addEventListener("click", (event) => {
        event.preventDefault();
        nextSlide(container, config, context);
    });
    prevButton.addEventListener("click", (event) => {
        event.preventDefault();
        prevSlide(container, config, context);
    });
}

function createDots(container, config, context) {
    if (!config.dots) {
        return;
    }
    const { slidesToShow, slidesToScroll } = config;
    const track = getSlidesTrack(container);
    const pagesCount = Math.ceil((track.children.length - slidesToShow) / slidesToScroll + 1);
    const dotsWrapper = dotListTemplate.getElement();

    Array.from(Array(pagesCount)).forEach((_, idx) => {
        const listItem = dotItemTemplate.getElement();
        const iden = getSlideIden(idx);

        const onDotClick = function(){
            if(this.closest('li').classList.contains(activeDotClassName))
                return;

            animateTrack(track, track.children[idx * slidesToScroll], config);
            context.setActiveSlide(idx * slidesToScroll);
            setActiveDot(container, config, context);
            config.slideCallback(track.children[idx]);
            setupAutoplay(container, config, context);
            event.stopPropagation();
        };

        listItem.id = `slick-slide${iden}`;
        listItem.setAttribute("aria-controls", `navigation${iden}`);
        listItem.addEventListener("click", onDotClick);

        const button = listItem.querySelector("button");
        button.textContent = idx;
        button.addEventListener("click", onDotClick);

        dotsWrapper.appendChild(listItem);
    });
    
    container.appendChild(dotsWrapper);
    setActiveDot(container, config, context);
}

function toggleScrollVisibilityEvents(config) {
    if (config.fullscreen) {
        document.dispatchEvent(new CustomEvent("scroll"));
    }
}

function setupMouseWheelHandler(container, config, context) {
    if (!config.useWheel) {
        return;
    }

    let isRunning = false;
    function scrollWheelHandler(event) {
        const isNext = config.vertical ? event.deltaY > 0 : event.deltaX > 0;
        if (isNext) {
            nextSlide(container, config, context);
        } else {
            prevSlide(container, config, context);
        }

        document.removeEventListener("wheel", scrollWheelHandler);

        setTimeout(() => {
            toggleScrollVisibilityEvents(config);
            document.addEventListener("wheel", scrollWheelHandler, { passive: true });
        }, 2000);
    }
    document.addEventListener("wheel", scrollWheelHandler, { passive: true });
}

function fireIntersectionObserverOnSwipe(config) {
    if (config.fullscreen) {
        setTimeout(() => {
            document.body.style.overflow = "auto";
            requestAnimationFrame(() => {
                document.body.style.overflow = "hidden";
            });
        }, config.speed);
    }
}

function setupTouchHandler(container, config, context) {
    const track = getSlidesTrack(container);
    let startTouchX = 0;
    let startTouchY = 0;
    let endTouchX = 0;
    let endTouchY = 0;
    let handleEvent = false;

    track.addEventListener("touchstart", (event) => {
        event.stopPropagation();
        startTouchX = event.touches[0].pageX;
        startTouchY = event.touches[0].pageY;
        handleEvent = false;
    });

    track.addEventListener("touchmove", (event) => {
        event.stopPropagation();
        endTouchX = event.touches[0].pageX;
        endTouchY = event.touches[0].pageY;
        handleEvent = true;
    });

    track.addEventListener("touchend", (event) => {
        let isRealScroll = true;

        if (!handleEvent) return;

        event.stopPropagation();

        if (config.vertical) {
            isRealScroll = Math.abs(startTouchY - endTouchY) > Math.abs(startTouchX - endTouchX);
        } else {
            isRealScroll = Math.abs(startTouchY - endTouchY) < Math.abs(startTouchX - endTouchX);
        }
        if (isRealScroll) {   
            if (config.vertical ? startTouchY > endTouchY : startTouchX > endTouchX) {
                nextSlide(container, config, context);
            } else {
                prevSlide(container, config, context);
            }
            fireIntersectionObserverOnSwipe(config);
        }
        startTouchX = endTouchX = 0;
        handleEvent = false;
    });
}

function setupAutoplay(container, config, context) {
    if (!config.autoplay) return;

    if (context.autoplayTimeout) clearTimeout(context.autoplayTimeout);

    context.autoplayTimeout = setTimeout(() => {
        const res = nextSlide(container, config, context);
    }, config.autoplaySpeed);
}

function setActiveDot(container, config, context) {
    if (!config.dots) {
        return;
    }

    const activeDot = container.querySelector(`.slick-dots li.${activeDotClassName}`);
    if (activeDot) {
        activeDot.classList.remove(activeDotClassName);
    }

    const iden = getSlideIden(Math.floor( context.getActiveSlide() / config.slidesToScroll ));
    const newDot = container.querySelector(`#slick-slide${iden}`);
    
    if (newDot) {
        newDot.classList.add(activeDotClassName);
    }
};

function createSlideContext() {
    let activeSlideId = 0;
    return {
        autoplayTimeout: null,
        setActiveSlide(idx) {
            activeSlideId = idx;
            return activeSlideId;
        },
        getActiveSlide() {
            return activeSlideId;
        },
        resetActiveSlide() {
            activeSlideId = 0;
            return activeSlideId;
        },
    };
}

function applyResponsiveConfig(config) {
    if (!config.responsive.length) {
        return config;
    }
    const responsiveConfig = config.responsive.find(
        (conf) => conf.breakpoint >= window.innerWidth
    );
    return responsiveConfig ? { ...config, ...responsiveConfig.settings } : config;
}

const defaultConfig = {
    arrows: true,
    dots: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 300,
    infinite: true,
    vertical: false,
    whiteSpace: "nowrap",
    fullscreen: false,
    useWheel: false,
    autoplay: false,
    autoplaySpeed: 3000,
    responsive: [],
    trackStyles: {},
    slideStyles: {},
    slideCallback: () => {},
};

export default function rebelSlider(config) {
    config = applyResponsiveConfig({ ...defaultConfig, ...config });
    document.querySelectorAll(config.selector).forEach(function(slideshow){
        if(slideshow.dataset.rebelSlider)
            return;

        slideshow.dataset.rebelSlider = true;

        const context = createSlideContext(config, slideshow);

        wrapSlides(slideshow, config);
        renderNavigationButtons(slideshow, config, context);
        createDots(slideshow, config, context);
        setupTouchHandler(slideshow, config, context);
        setupAutoplay(slideshow, config, context);
        setupMouseWheelHandler(slideshow, config, context);
    });
}
